
/** Zone color hex values */
export const ZONE_COLORS = {
  good: "#22c55e", // green-500
  warning: "#f59e0b", // amber-500
  bad: "#ef4444", // red-500
};

/** All threshold value field names (excludes the `thresholdType` selector itself). */
export const THRESHOLD_VALUE_FIELDS = [
  "thresholdTarget",
  "thresholdWarningPercentage",
  "thresholdTargetPerDay",
  "thresholdLowerThreshold",
  "thresholdUpperThreshold",
  "thresholdLowerThresholdPerDay",
  "thresholdUpperThresholdPerDay",
];

/**
 * Value fields that apply to each threshold type. Fields not listed for a type
 * are always null when that type is active; the Settings UI never exposes them.
 *
 * `more_the_better` and `less_the_better` share the same field set so users can
 * switch between them without losing their target/warning inputs — only the
 * category change (to/from `optimal_range`, or to null) resets values.
 */
const RELEVANT_FIELDS_BY_TYPE = {
  more_the_better: [
    "thresholdTarget",
    "thresholdWarningPercentage",
    "thresholdTargetPerDay",
  ],
  less_the_better: [
    "thresholdTarget",
    "thresholdWarningPercentage",
    "thresholdTargetPerDay",
  ],
  optimal_range: [
    "thresholdLowerThreshold",
    "thresholdUpperThreshold",
    "thresholdLowerThresholdPerDay",
    "thresholdUpperThresholdPerDay",
  ],
};

/**
 * Projects a threshold config onto the given `thresholdType`, returning an
 * object with all THRESHOLD_VALUE_FIELDS populated: relevant fields carry the
 * numeric value from `config` (or null if missing/non-numeric), every other
 * field is null. When `thresholdType` is falsy or unknown, every field is null.
 *
 * Callers must spread the result over the config object so fields outside the
 * active type are reset rather than carried forward as stale values.
 */
export function sanitizeThresholdFields(config, thresholdType) {
  const relevant = thresholdType
    ? RELEVANT_FIELDS_BY_TYPE[thresholdType] || []
    : [];
  const result = {};
  for (const field of THRESHOLD_VALUE_FIELDS) {
    if (relevant.includes(field)) {
      const v = config?.[field];
      result[field] = typeof v === "number" ? v : null;
    } else {
      result[field] = null;
    }
  }
  return result;
}

/**
 * Validates threshold configuration and returns a warning/error message.
 * Returns null if configuration is valid.
 *
 * Validation rules:
 * - "more the better": warningPercentage should be < 100
 * - "less the better": warningPercentage should be > 100
 * - "optimal range": lowerThreshold must be < upperThreshold
 *
 * @param {string} thresholdType - The threshold type.
 * @param {Object} config - Threshold configuration values.
 * @returns {string|null} Warning/error message or null if valid.
 */
export function validateThresholdConfig(thresholdType, config) {
  if (!thresholdType) return null;

  if (thresholdType === "optimal_range") {
    if (config.lowerThreshold != null && config.upperThreshold != null) {
      if (config.lowerThreshold >= config.upperThreshold) {
        return "Lower threshold must be less than upper threshold";
      }
    }
    if (
      config.lowerThresholdPerDay != null &&
      config.upperThresholdPerDay != null
    ) {
      if (config.lowerThresholdPerDay >= config.upperThresholdPerDay) {
        return "Lower threshold per day must be less than upper threshold per day";
      }
    }
  }

  if (thresholdType === "more_the_better" && config.warningPercentage != null) {
    if (config.warningPercentage >= 100) {
      return 'Warning percentage should be less than 100% for "The more the better"';
    }
  }

  if (thresholdType === "less_the_better" && config.warningPercentage != null) {
    if (config.warningPercentage <= 100) {
      return 'Warning percentage should be greater than 100% for "The less the better"';
    }
  }

  return null;
}
